// Which roster automation reductions apply to.
//
// Cost savings for hub/OGS are always measured against the client's Actual plan,
// but automation release can reasonably be projected onto the modeled team, the
// modeled team with manual edits, or the real one. The Automation Impact tab owns
// the choice; the Summary page follows it, so the resolution lives here rather
// than in either view.
//
// Basic scenarios lock the basis to the staff plan after OGS proportion split
// (`staffPlanToOgsAdjustedBasisCells`) — there is no Modeled path.

import {
  applyStaffPlanOverrides,
  isActualStaffPlan,
  mapCellToSubDiscKey,
  staffPlanToBasisCells,
  staffPlanToOgsAdjustedBasisCells,
} from '@rc/engine';
import type {
  AutomationBasis,
  CalculationResult,
  ClientProfile,
  DisciplineChannelCell,
  Model,
  ScenarioWorkspace,
  StaffPlan,
} from '@rc/engine/types';

export interface BasisOption {
  id: AutomationBasis;
  label: string;
  sub: string;
}

/** Only offer a basis we can actually build. Modeled-Adjusted needs at least one
 *  manual edit, and Actual needs an attached plan that really records the
 *  client's team — a generated plan must never masquerade as one.
 *  Basic scenarios only offer the OGS-adjusted staff plan. */
export function availableBases(
  profile: ClientProfile,
  staffPlan: StaffPlan | null | undefined,
  workspace: ScenarioWorkspace = 'advanced',
): BasisOption[] {
  if (workspace === 'basic') {
    if (isActualStaffPlan(staffPlan) && (staffPlan?.cells?.length || 0) > 0) {
      return [
        {
          id: 'actual',
          label: 'Staff plan',
          sub: `Selected plan after OGS hub proportion (${staffPlan?.name || 'staff plan'})`,
        },
      ];
    }
    return [];
  }

  const out: BasisOption[] = [
    {
      id: 'modeled',
      label: 'Modeled',
      sub: 'The team the model sizes from budget',
    },
  ];
  if (Object.keys(profile.staffPlanOverrides || {}).length > 0) {
    out.push({
      id: 'modeled_adjusted',
      label: 'Modeled-Adjusted',
      sub: 'The modeled team with your manual edits',
    });
  }
  if (isActualStaffPlan(staffPlan) && (staffPlan?.cells?.length || 0) > 0) {
    out.push({
      id: 'actual',
      label: 'Actual',
      sub: `The team on ${staffPlan?.name || 'the attached staff plan'}`,
    });
  }
  return out;
}

/** The saved choice, falling back to Modeled when it is no longer buildable —
 *  an edit can be reverted or a plan detached after the choice was made.
 *  Basic always resolves to Actual when that option exists. */
export function resolveBasis(
  profile: ClientProfile,
  options: BasisOption[],
  workspace: ScenarioWorkspace = 'advanced',
): AutomationBasis {
  if (workspace === 'basic') {
    return options.some((o) => o.id === 'actual') ? 'actual' : 'modeled';
  }
  return options.some((o) => o.id === profile.automationBasis)
    ? (profile.automationBasis as AutomationBasis)
    : 'modeled';
}

/**
 * Whether adjusting a cell should also re-point the automation basis, and to
 * what. Returns undefined to leave the basis alone.
 *
 * The Summary page stacks the editable grid directly above the post-automation
 * matrix derived from it, so an edit that did not reach the basis would leave
 * the two cards disagreeing about the same team. Switching on the *first* edit
 * fixes that; after that the basis is a visible choice the user has made, and
 * an Actual basis anchors on the client's real roster either way.
 */
export function basisAfterAdjustment(
  current: AutomationBasis | undefined,
  hadOverrides: boolean,
  hasOverrides: boolean,
): AutomationBasis | undefined {
  if (!hasOverrides || hadOverrides) return undefined;
  return (current ?? 'modeled') === 'modeled' ? 'modeled_adjusted' : undefined;
}

export function basisLabel(basis: AutomationBasis): string {
  switch (basis) {
    case 'modeled_adjusted':
      return 'Modeled-Adjusted';
    case 'actual':
      return 'Actual';
    default:
      return 'Modeled';
  }
}

/** The staffing cells a basis resolves to. */
export function basisCells(
  basis: AutomationBasis,
  model: Model,
  result: CalculationResult,
  profile: ClientProfile,
  staffPlan: StaffPlan | null | undefined,
  workspace: ScenarioWorkspace = 'advanced',
): DisciplineChannelCell[] {
  if (workspace === 'basic') {
    return staffPlanToOgsAdjustedBasisCells(staffPlan, model, profile);
  }
  const modeled = result.byDisciplineChannelLevel || [];
  if (basis === 'actual') return staffPlanToBasisCells(staffPlan, model);
  if (basis === 'modeled_adjusted') {
    return applyStaffPlanOverrides(modeled, profile.staffPlanOverrides, model).cells;
  }
  return modeled;
}

/**
 * How much of a basis can release hours at all.
 *
 * Automation savings come from the model's time-concentration table, keyed by
 * sub-discipline and level. Headcount sitting at a (sub-discipline, level) the
 * table says nothing about contributes zero no matter which automations are
 * switched on — which is easy to miss when the basis is a hand-entered plan
 * that covers roles the model does not.
 */
export function basisCoverage(
  model: Model,
  cells: DisciplineChannelCell[],
): { total: number; covered: number; uncovered: number } {
  const hasShare = new Set<string>();
  for (const row of model.timeConcentration || []) {
    if ((Number(row.share) || 0) > 0) {
      hasShare.add(`${row.subDisciplineKey}::${row.levelId}`);
    }
  }

  let total = 0;
  let covered = 0;
  for (const c of cells) {
    const fte = Number(c.adjustedFte) || 0;
    if (fte <= 0) continue;
    total += fte;
    const key = mapCellToSubDiscKey(model, c);
    if (key && hasShare.has(`${key}::${c.levelId}`)) covered += fte;
  }
  return { total, covered, uncovered: total - covered };
}
